import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, LogOut, Shield, User, AlertTriangle, CheckCircle, XCircle,
  Clock, RefreshCw, Ban, Edit3, ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { BACKEND_URL } from '../config/api';

const STATUS_STYLES = {
  flagged_for_review: { label: 'Flagged', icon: AlertTriangle, cls: 'bg-yellow-500/20 text-yellow-700 border-yellow-300' },
  needs_changes: { label: 'Needs Changes', icon: RefreshCw, cls: 'bg-orange-500/20 text-orange-700 border-orange-300' },
  rejected_agency: { label: 'Rejected (Agency)', icon: XCircle, cls: 'bg-red-500/20 text-red-700 border-red-300' },
  suspended: { label: 'Suspended', icon: Ban, cls: 'bg-slate-500/20 text-slate-700 border-slate-300' },
  approved: { label: 'Approved', icon: CheckCircle, cls: 'bg-green-500/20 text-green-700 border-green-300' },
};

const WORKER_LABELS = {
  individual_tradesperson: 'Individual',
  self_employed_subcontractor: 'Subcontractor',
  small_trade_team_gang: 'Trade Team',
  limited_company_trade_contractor: 'Ltd Company',
};

function StatusBadge({ status }) {
  const cfg = STATUS_STYLES[status] || STATUS_STYLES.flagged_for_review;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${cfg.cls}`} data-testid={`mod-status-${status}`}>
      <Icon className="h-3 w-3" /> {cfg.label}
    </span>
  );
}

export default function AdminModerationQueue() {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('flagged_for_review');
  const [reasons, setReasons] = useState({});

  // Review modal
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [action, setAction] = useState('');
  const [reason, setReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [newWorkerType, setNewWorkerType] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/moderation-queue`, {
        params: { status: statusFilter },
        withCredentials: true,
      });
      setProfiles(res.data);
    } catch { toast.error('Failed to load moderation queue'); }
    setLoading(false);
  }, [statusFilter]);

  const fetchReasons = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/moderation-reasons`, { withCredentials: true });
      setReasons(res.data.reasons || {});
    } catch {}
  }, []);

  useEffect(() => { fetchReasons(); }, [fetchReasons]);
  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const openReview = (profile) => {
    setSelected(profile);
    setAction('');
    setReason('');
    setAdminNotes('');
    setNewWorkerType(profile.worker_type || profile.user?.worker_type || '');
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!action) { toast.error('Select an action'); return; }
    if ((action === 'request_changes' || action === 'reject_agency' || action === 'suspend') && !reason) {
      toast.error('Select a reason'); return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/moderation/${selected.user_id}/review`,
        { action, reason, admin_notes: adminNotes || undefined },
        { withCredentials: true }
      );
      toast.success(`Profile ${action.replace(/_/g, ' ')}`);
      setShowModal(false);
      fetchQueue();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    setSubmitting(false);
  };

  const handleWorkerTypeChange = async () => {
    if (!newWorkerType) return;
    setSubmitting(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${selected.user_id}/worker-type`,
        { worker_type: newWorkerType },
        { withCredentials: true }
      );
      toast.success('Worker type updated');
      fetchQueue();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSubmitting(false);
  };

  const tabs = [
    { value: 'flagged_for_review', label: 'Flagged' },
    { value: 'needs_changes', label: 'Needs Changes' },
    { value: 'rejected_agency', label: 'Rejected' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'all', label: 'All' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <header className="bg-[#12121a] border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-orange-500" />
              <span className="text-xl font-bold text-white">Profile Moderation</span>
              {profiles.length > 0 && statusFilter === 'flagged_for_review' && (
                <span className="ml-2 bg-yellow-500 text-black text-xs font-bold px-2 py-0.5 rounded-full" data-testid="queue-count">{profiles.length}</span>
              )}
            </div>
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin/dashboard')} className="text-gray-400 hover:text-white text-sm">Dashboard</button>
              <button onClick={() => navigate('/admin/verification-queue')} className="text-gray-400 hover:text-white text-sm">Credentials</button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="mod-filter-tabs">
          {tabs.map(tab => (
            <button key={tab.value} onClick={() => setStatusFilter(tab.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                statusFilter === tab.value ? 'bg-orange-500 text-white' : 'bg-[#12121a] border border-gray-700 text-gray-400 hover:text-white'
              }`} data-testid={`mod-filter-${tab.value}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Queue */}
        <div className="bg-[#12121a] border border-gray-800 rounded-xl overflow-hidden" data-testid="moderation-list">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500" />
            </div>
          ) : profiles.length > 0 ? (
            <div className="divide-y divide-gray-800">
              {profiles.map(p => (
                <div key={p.user_id} className="p-4 hover:bg-[#0a0a0f]/50 transition" data-testid={`mod-row-${p.user_id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <StatusBadge status={p.moderation_status} />
                        <span className="text-white font-semibold text-sm">{p.user?.name || 'Unknown'}</span>
                        <span className="text-xs bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded">
                          {WORKER_LABELS[p.worker_type || p.user?.worker_type] || p.worker_type}
                        </span>
                      </div>
                      <p className="text-gray-500 text-xs mb-2">{p.user?.email} | Trade: {p.trade || 'Not set'}</p>
                      {p.moderation_reason && (
                        <p className="text-sm text-orange-400 mb-1">Reason: {p.moderation_reason}</p>
                      )}
                      {p.moderation_flagged_at && (
                        <p className="text-xs text-gray-600">Flagged: {new Date(p.moderation_flagged_at).toLocaleDateString()}</p>
                      )}
                      {/* Show key profile fields */}
                      <div className="mt-2 flex flex-wrap gap-2">
                        {p.trading_name && <span className="text-xs bg-gray-800 text-gray-300 px-2 py-0.5 rounded">Trading: {p.trading_name}</span>}
                        {p.team_name && <span className="text-xs bg-gray-800 text-gray-300 px-2 py-0.5 rounded">Team: {p.team_name}</span>}
                        {p.registered_company_name && <span className="text-xs bg-gray-800 text-gray-300 px-2 py-0.5 rounded">Company: {p.registered_company_name}</span>}
                      </div>
                    </div>
                    <button onClick={() => openReview(p)}
                      className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition text-sm font-medium flex-shrink-0"
                      data-testid={`mod-review-btn-${p.user_id}`}>
                      <Shield className="w-3.5 h-3.5" /> Review
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16" data-testid="mod-empty">
              <CheckCircle className="w-12 h-12 text-green-500/30 mx-auto mb-3" />
              <p className="text-gray-500">No profiles for this filter</p>
            </div>
          )}
        </div>
      </main>

      {/* Review Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#12121a] border border-gray-800 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review Profile</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 mt-2" data-testid="mod-review-modal">
              {/* Profile summary */}
              <div className="p-3 bg-[#0a0a0f] rounded-lg space-y-1">
                <p className="text-white font-medium">{selected.user?.name}</p>
                <p className="text-gray-500 text-sm">{selected.user?.email}</p>
                <p className="text-gray-500 text-sm">Worker type: {WORKER_LABELS[selected.worker_type || selected.user?.worker_type] || 'Unknown'}</p>
                <p className="text-gray-500 text-sm">Trade: {selected.trade || 'Not set'}</p>
                {selected.moderation_reason && <p className="text-orange-400 text-sm">Flag reason: {selected.moderation_reason}</p>}
                {selected.trading_name && <p className="text-gray-400 text-sm">Trading name: {selected.trading_name}</p>}
                {selected.team_name && <p className="text-gray-400 text-sm">Team name: {selected.team_name}</p>}
                {selected.registered_company_name && <p className="text-gray-400 text-sm">Company: {selected.registered_company_name}</p>}
                {selected.bio && <p className="text-gray-400 text-sm mt-2 italic">"{selected.bio.slice(0, 200)}{selected.bio.length > 200 ? '...' : ''}"</p>}
              </div>

              {/* Action buttons */}
              <div className="space-y-2">
                <p className="text-xs text-gray-400 uppercase tracking-wide">Action</p>
                <div className="grid grid-cols-2 gap-2" data-testid="mod-actions">
                  {[
                    { val: 'approve', label: 'Approve', icon: CheckCircle, cls: 'border-green-500/40 text-green-400 bg-green-500/10' },
                    { val: 'request_changes', label: 'Request Changes', icon: RefreshCw, cls: 'border-orange-500/40 text-orange-400 bg-orange-500/10' },
                    { val: 'reject_agency', label: 'Reject (Agency)', icon: XCircle, cls: 'border-red-500/40 text-red-400 bg-red-500/10' },
                    { val: 'suspend', label: 'Suspend', icon: Ban, cls: 'border-gray-500/40 text-gray-400 bg-gray-500/10' },
                    { val: 'clear_flag', label: 'Clear Flag', icon: CheckCircle, cls: 'border-blue-500/40 text-blue-400 bg-blue-500/10' },
                  ].map(a => (
                    <button key={a.val} onClick={() => setAction(a.val)}
                      className={`flex items-center gap-2 p-2 rounded-lg border text-xs transition ${
                        action === a.val ? a.cls : 'border-gray-700 text-gray-500 bg-[#0a0a0f] hover:border-gray-600'
                      }`} data-testid={`mod-action-${a.val}`}>
                      <a.icon className="h-3.5 w-3.5" /> {a.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reason selector (for request_changes, reject, suspend) */}
              {(action === 'request_changes' || action === 'reject_agency' || action === 'suspend') && (
                <div data-testid="mod-reason-section">
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Reason <span className="text-red-400">*</span></p>
                  <div className="grid grid-cols-1 gap-1.5">
                    {Object.entries(reasons).map(([key, label]) => (
                      <button key={key} onClick={() => setReason(key)}
                        className={`text-left p-2 rounded-lg border text-xs transition ${
                          reason === key ? 'border-orange-500/50 bg-orange-500/10 text-orange-300' : 'border-gray-700 text-gray-400 hover:border-gray-600'
                        }`} data-testid={`mod-reason-${key}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin notes */}
              <div>
                <label className="block text-xs text-gray-400 mb-1">Admin Notes (optional)</label>
                <textarea value={adminNotes} onChange={e => setAdminNotes(e.target.value)} rows={2}
                  className="w-full bg-[#0a0a0f] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none"
                  placeholder="Internal note..." data-testid="mod-admin-notes" />
              </div>

              {/* Submit */}
              <button onClick={handleSubmit} disabled={!action || submitting}
                className="w-full py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 font-medium text-sm"
                data-testid="mod-submit-btn">
                {submitting ? 'Submitting...' : 'Submit Decision'}
              </button>

              <hr className="border-gray-800" />

              {/* Edit Worker Type */}
              <div className="space-y-2">
                <p className="text-xs text-gray-400 uppercase tracking-wide">Edit Worker Type</p>
                <div className="flex gap-2">
                  <select value={newWorkerType} onChange={e => setNewWorkerType(e.target.value)}
                    className="flex-1 bg-[#0a0a0f] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                    data-testid="mod-worker-type-select">
                    <option value="individual_tradesperson">Individual tradesperson</option>
                    <option value="self_employed_subcontractor">Self-employed subcontractor</option>
                    <option value="small_trade_team_gang">Small trade team/gang</option>
                    <option value="limited_company_trade_contractor">Limited company trade contractor</option>
                  </select>
                  <button onClick={handleWorkerTypeChange} disabled={submitting}
                    className="px-3 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 text-sm disabled:opacity-50"
                    data-testid="mod-change-type-btn">
                    <Edit3 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
